// ── antimeridian helpers ──
// deck.gl projects longitude linearly and draws a segment from −179° to
// +179° the long way around the world (its `wrapLongitude` only normalises
// individual vertices — it cannot split a segment). Central-Pacific storms
// recurving past 180° (Lowell, EP12 2026) therefore rendered as horizontal
// lines circling the earth. The fix is data-side: express every path with
// CONTINUOUS longitudes (−179 → −181, not +179), anchored near a reference
// longitude so it lands in the world copy the viewer is looking at.

/** Longitude re-expressed within ±180° of `ref` (may leave [−180, 180]). */
export function lonNear(lon: number, ref: number): number {
  let d = lon - ref;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return ref + d;
}

/**
 * Make a [lon, lat] path continuous across the antimeridian. Consecutive
 * longitude jumps > 180° are unwrapped by ±360°; with `ref` given, the first
 * vertex is also anchored within ±180° of it. Returns the input untouched
 * (same reference) when nothing needs changing.
 */
export function unwrapPath(path: [number, number][], ref?: number): [number, number][] {
  if (!Array.isArray(path) || path.length === 0) return path;
  let out: [number, number][] | null = null;
  let prev = ref === undefined ? path[0][0] : lonNear(path[0][0], ref);
  if (prev !== path[0][0]) out = [[prev, path[0][1]]];
  for (let i = 1; i < path.length; i++) {
    const raw = path[i][0];
    const lon = lonNear(raw, prev);
    if (lon !== raw && !out) out = path.slice(0, i);
    if (out) out.push([lon, path[i][1]]);
    prev = lon;
  }
  return out ?? path;
}

/** Polygon = one ring or [outer, ...holes]; unwrap each ring. */
export function unwrapPolygon(poly: unknown, ref?: number): unknown {
  if (!Array.isArray(poly) || poly.length === 0) return poly;
  const first = poly[0];
  if (Array.isArray(first) && Array.isArray(first[0])) {
    return (poly as [number, number][][]).map((ring) => unwrapPath(ring, ref));
  }
  return unwrapPath(poly as [number, number][], ref);
}

/** Bounding box of a path after unwrapping — tight even across the dateline. */
export function unwrappedBounds(
  pts: [number, number][],
): [[number, number], [number, number]] | null {
  if (pts.length < 2) return null;
  const u = unwrapPath(pts);
  const lons = u.map((p) => p[0]);
  const lats = u.map((p) => p[1]);
  return [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];
}
